import { Injectable } from '@angular/core';

interface NetworkInformation {
  downlink: number;
  effectiveType: string;
  rtt: number;
  saveData: boolean;
}

/**
 * @description
 * Service to interact with the browser's Navigator object and Network Information API.
 * Provides methods to access various properties of the Navigator and Network Information.
 *
 * @example
 * // Injecting NavigatorService in a component
 * constructor(private navigatorService: NavigatorService) {}
 *
 * // Using NavigatorService methods
 * const userAgent = this.navigatorService.getUserAgent();
 * const language = this.navigatorService.getLanguage();
 * const geolocation = this.navigatorService.getGeolocation();
 * const connection = this.navigatorService.getConnection();
 * const networkType = this.navigatorService.getNetworkType();
 * const downlinkSpeed = this.navigatorService.getDownlinkSpeed();
 * const rtt = this.navigatorService.getRTT();
 * const isSaveDataEnabled = this.navigatorService.isSaveDataEnabled();
 */
@Injectable({
  providedIn: 'root'
})
export class NavigatorService {
    private connection: NetworkInformation | undefined = 
    (navigator as any).connection || 
    (navigator as any).mozConnection || 
    (navigator as any).webkitConnection;

/**
 * Retrieves the Navigator object from the global window object if it is defined.
 *
 * @returns {Navigator | undefined} The Navigator object if the window object is defined, otherwise undefined.
 */
  getNavigator(): Navigator | undefined {
    return typeof window !== 'undefined' ? window.navigator : undefined;
  }

/**
 * Retrieves the user agent string from the browser's navigator object.
 *
 * @returns {string | undefined} The user agent string if available, otherwise undefined.
 */
  getUserAgent(): string | undefined {
    return this.getNavigator()?.userAgent;
  }

/**
 * Retrieves the language setting from the browser's navigator object.
 *
 * @returns {string | undefined} The language setting of the browser, or undefined if the navigator object is not available.
 */
  getLanguage(): string | undefined {
    return this.getNavigator()?.language;
  }

/**
 * Retrieves the Geolocation object from the browser's Navigator object.
 *
 * @returns {Geolocation | undefined} The Geolocation object if available, otherwise undefined.
 */
  getGeolocation(): Geolocation | undefined {
    return this.getNavigator()?.geolocation;
  }

/**
 * Retrieves the current network connection information.
 *
 * @returns {NetworkInformation | undefined} The network connection information if available, otherwise undefined.
 */
  getConnection(): NetworkInformation | undefined {
    return this.connection;
  }

/**
 * Retrieves the effective network type of the user's connection.
 * 
 * @returns {string | undefined} The effective network type (e.g., '4g', '3g', '2g', 'slow-2g') if available, otherwise `undefined`.
 */
  getNetworkType(): string | undefined {
    return this.connection?.effectiveType;
  }

/**
 * Retrieves the downlink speed of the current network connection.
 *
 * @returns {number | undefined} The downlink speed in megabits per second (Mbps) if available, otherwise undefined.
 */
  getDownlinkSpeed(): number | undefined {
    return this.connection?.downlink;
  }

/**
 * Retrieves the Round-Trip Time (RTT) of the current network connection.
 *
 * @returns {number | undefined} The RTT in milliseconds if available, otherwise undefined.
 */
  getRTT(): number | undefined {
    return this.connection?.rtt;
  }

/**
 * Checks if the Save-Data mode is enabled in the browser.
 *
 * The Save-Data mode is a user preference that requests web pages to use less data.
 * This method returns a boolean indicating whether the Save-Data mode is enabled,
 * or `undefined` if the connection information is not available.
 *
 * @returns {boolean | undefined} `true` if Save-Data mode is enabled, `false` if it is not,
 * or `undefined` if the connection information is not available.
 */
  isSaveDataEnabled(): boolean | undefined {
    return this.connection?.saveData;
  }
}

